<template>
  <transition name="prompt-menu">
    <div v-if="isOpen" class="suggestions-wrapper">
      <div class="menu-backdrop" @click="$emit('close')"></div>

      <div
        class="suggestions-panel"
        :class="{ 'suggestions-panel--compact': isCompact }"
        @click.stop
      >
        <div class="panel-header">
          <div class="header-content">
            <p class="panel-title">Prompt Templates</p>
          </div>
          <span class="panel-hint">Click to use</span>
          <span
            v-if="patientModeEnabled && patientName"
            class="patient-indicator"
            :title="patientName"
          >
            <i class="fas fa-user-check"></i>
            {{ patientName }}
          </span>
        </div>

        <div class="prompt-grid">
          <div
            v-for="category in promptCategories"
            :key="category.label"
            class="prompt-category"
          >
            <p class="category-label" :style="{ color: category.color }">
              {{ category.label }}
            </p>

            <div class="prompt-list">
              <button
                v-for="prompt in category.prompts"
                :key="prompt.label"
                class="prompt-button"
                @click="handlePromptSelect(prompt.value)"
              >
                <i
                  :class="['fas', prompt.icon, 'prompt-icon']"
                  :style="{ color: category.color }"
                ></i>
                <span class="prompt-text">{{ prompt.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { getPatientPlaceholder } from "@/components/OliverBotV2/utils/patientModeManager.js";
import { mapActions, mapState } from "vuex";
import { USER_ROLES } from "./constants";

const DEFAULT_PROMPT_ICON = "fa-comment";
const DEFAULT_GROUP_COLOR = "#0d9488";

export default {
  name: "SuggestionsPanel",
  components: {},
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      default: "chat",
      validator: (value) => ["welcome", "chat"].includes(value),
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    isWide: {
      type: Boolean,
      default: false,
    },
    patientModeEnabled: {
      type: Boolean,
      default: false,
    },
    webSearchEnabled: {
      type: Boolean,
      default: false,
    },
    showPatientMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      currentPatient: (state) => state.patients.patient,
      currentUser: (state) => state.auth.user,
      promptTemplateGroups: (state) => state.promptTemplates.groups,
    }),
    patientName() {
      if (!this.currentPatient) return "";
      const firstName =
        this.currentPatient.first || this.currentPatient.user?.first || "";
      const lastName =
        this.currentPatient.last || this.currentPatient.user | "";
      return lastName ? `${firstName} ${lastName}`.trim() : firstName;
    },
    userRole() {
      if (this.currentUser?.isAdmin) return USER_ROLES.ADMIN;
      if (this.currentUser?.isManagingAdmin) return USER_ROLES.MANAGER;
      return USER_ROLES.PROVIDER;
    },
    promptCategories() {
      return this.promptTemplateGroups
        .filter((group) => (group.prompts || []).length > 0)
        .map((group) => ({
          label: group.name,
          color: group.color || DEFAULT_GROUP_COLOR,
          prompts: (group.prompts || []).map((prompt) => ({
            icon: prompt.icon || DEFAULT_PROMPT_ICON,
            label: this.resolvePromptText(prompt.label),
            value: prompt.value,
          })),
        }));
    },
    isCompact() {
      return !this.isFullscreen && !this.isWide;
    },
    chatInputPlaceholder() {
      if (this.patientModeEnabled && this.currentPatient) {
        return getPatientPlaceholder(this.currentPatient);
      }
      return "Ask anything...";
    },
  },
  mounted() {
    this.fetchPromptTemplates();
  },
  methods: {
    ...mapActions("promptTemplates", ["fetchPromptTemplates"]),
    resolvePromptText(text) {
      if (this.patientModeEnabled && this.patientName) {
        return text.replace(/\[name\]/g, this.patientName);
      }
      return text;
    },
    handlePromptSelect(value) {
      this.$emit("select-command", {
        prompt: this.resolvePromptText(value),
        files: [],
      });
      this.$emit("close");
    },
    handleCommandClick(command) {
      if (command.action === "navigate" && command.route) {
        this.$router.push(command.route);
        this.$emit("close");
        this.$emit("close-oliver");
        return;
      }
      this.$emit("select-command", { prompt: command.prompt, files: [] });
      this.$emit("close");
    },
    handleTogglePatientMode(isEnabled) {
      this.$emit("toggle-patient-mode", isEnabled);
    },
    handleToggleWebSearch(isEnabled) {
      this.$emit("toggle-web-search", isEnabled);
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes panel-slide-in {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes panel-slide-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
}

.prompt-menu-enter-active {
  transition: opacity 0.2s ease;
}

.prompt-menu-leave-active {
  transition: opacity 0.2s ease;
}

.prompt-menu-enter-active .suggestions-panel {
  animation: panel-slide-in 0.2s ease forwards;
}

.prompt-menu-leave-active .suggestions-panel {
  animation: panel-slide-out 0.2s ease forwards;
}

.prompt-menu-enter,
.prompt-menu-leave-to {
  opacity: 0;
}

.suggestions-wrapper {
  position: absolute;
  top: 68px;
  left: 0;
  right: 0;
  bottom: 150px;
  z-index: 120;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 14px 10px;
  pointer-events: none;
  overflow: hidden;
}

.menu-backdrop {
  position: absolute;
  inset: 0;
  pointer-events: all;
}

.suggestions-panel {
  position: relative;
  z-index: 1;
  width: min(1120px, 100%);
  max-height: 100%;
  border-radius: 18px;
  border: 1px solid var(--oliver-border);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  pointer-events: all;
  background: var(
    --oliver-suggestions-bg,
    linear-gradient(160deg, #fdf8f4 0%, #fff9f6 100%)
  );
  box-shadow: 0 -4px 28px var(--oliver-indicator-border),
    0 4px 10px rgba(0, 0, 0, 0.08);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(233, 230, 226, 0.3);
  background: transparent;
}

.header-content {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.panel-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #1a1a1a;
}

.panel-hint {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.5);
}

.patient-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 40%;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--oliver-accent-border);
  background: var(--oliver-accent-light);
  color: var(--oliver-accent);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.prompt-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 0;
  overflow-y: auto;
}

.prompt-category {
  padding: 14px;
  border-right: 1px solid rgba(233, 230, 226, 0.3);
  border-bottom: 1px solid rgba(233, 230, 226, 0.3);
}

.prompt-category:nth-child(2n) {
  border-right: none;
}

.category-label {
  margin: 0 0 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.prompt-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.prompt-button {
  width: 100%;
  text-align: left;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid transparent;
  color: rgba(0, 0, 0, 0.8);
  background: transparent;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.8);
    color: #1a1a1a;
  }
}

.prompt-icon {
  margin-top: 2px;
  flex-shrink: 0;
  font-size: 12px;
  opacity: 0.8;
}

.prompt-text {
  line-height: 1.3;
  font-size: 12px;
}

::v-deep .chat-input-container {
  margin: 6px;
  margin-bottom: 3px;
  border-radius: 14px;
}

.quick-actions-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  border-top: 1px solid rgba(233, 230, 226, 0.3);
  background: var(--oliver-bg-surface, var(--oliver-bg));
}

.quick-action-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(233, 230, 226, 0.3);
  background: var(--oliver-btn-subtle-bg);
  color: var(--oliver-text-muted);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--oliver-btn-subtle-hover);
    color: var(--oliver-text);
    border-color: var(--oliver-accent-border);
  }

  i {
    color: var(--oliver-accent);
    font-size: 12px;
  }
}

@media (min-width: 1024px) {
  .prompt-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .prompt-category {
    border-right: 1px solid rgba(233, 230, 226, 0.3);
    border-bottom: none;
  }

  .prompt-category:nth-child(2n) {
    border-right: 1px solid rgba(233, 230, 226, 0.3);
  }

  .prompt-category:last-child {
    border-right: none;
  }
}

@media (max-width: 767px) {
  .suggestions-wrapper {
    top: 58px;
    padding: 0 8px 8px;
  }

  .suggestions-panel {
    max-height: 100%;
  }

  .panel-header {
    padding: 10px 12px;
  }

  .prompt-category {
    padding: 10px;
  }

  .prompt-button {
    padding: 8px;
    gap: 7px;
  }

  .patient-indicator {
    max-width: 46%;
    font-size: 10px;
  }
}

// Compact layout — applied when Oliver is in its default (non-expanded) size
.suggestions-panel--compact {
  .panel-header {
    padding: 10px 12px;
  }

  .prompt-grid {
    grid-template-columns: 1fr;
  }

  .prompt-category {
    border-right: none;
    border-bottom: 1px solid rgba(233, 230, 226, 0.3);
    padding: 10px;

    &:last-child {
      border-bottom: none;
    }
  }

  .prompt-category:nth-child(2n) {
    border-right: none;
  }

  .prompt-button {
    padding: 8px;
    gap: 7px;
  }

  .patient-indicator {
    max-width: 46%;
    font-size: 10px;
  }
}
</style>
